import {
  authActions, merlinActions, onboardingActions, preChatActions,
} from '@/redux/modules';
import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as Styles from './AiChat.styles';
import { AiInitScreen } from './components/AiInitScreen';
import {
  COUNTRY, ENVIRONMENT, IS_PROD,
  WSS_AI_ENV,
} from '@/config';
import useLocale from '@/hooks/useLocale';
import {
  Button, IconArrow, IconClose, IconMinus, Tag, IconExpand,
  Tooltip,
  IconEllipsis,
  IconAudioRecord,
  IconMiniminize,
} from 'gatorcomponents';
import { ChatHistory } from './components/ChatHistory';
import {
  DEVELOPMENT_ENV, INTERNAL_REDIRECT_OPTIONS, SCREENS, WSS_ROUTES,
} from './AiChat.enum';
import { useFeatureIsOn, useFeatureValue } from '@growthbook/growthbook-react';
import {
  ATIVACAO_AICHAT_AUDIO_RECORDING,
  ATIVACAO_AICHAT_RATING_SURVEY,
  ATIVACAO_DISABLE_TEXTEAREA_IF_OPTIONS, ATIVACAO_N8N_INHOUSE_INITIAL_SCREEN, ATIVACAO_N8N_INHOUSE_URLS, ATIVACAO_N8N_TIME_TO_RECONNECT,
  ATIVACAO_OPEN_CHAT_ON_LOGIN,
} from '@/config/GrowthBook/constants';
import logger from '@/utils/logger';
import { N8N_WSS } from '@/config/api';
import IconAnimation from '@/components/BannerAI/media/AiAnimation.webp';
import MaiaIconImage from '@/media/MaiaIA/iconImage.png';
import { useHistory } from 'react-router';
import { AudioRecording } from './components/AudioRecording';
import { IconButton } from '@/pages/common/v1/IconButton';
import { TranscriptionSkeleton } from './components/AudioRecording/TrancriptionSkeleton';
import { collect } from '@/analytics';
import { SettingsPopover } from './components/SettingsPopover';
import { SideContent } from './components/SideContent';
import { useResize } from '@/hooks/useResize';


export const AiChat = ({
  handleClose,
  minimizedChat,
  handleToggleMinimize,
  visibleAiChat,
  setVisibleAiChat,
  renderInHouseChat,
}) => {
  const socket = useRef(null);
  const restartIntervalRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);

  const dispatch = useDispatch();

  const {
    token,
    chatHistory: history,
    audioTranscription,
    loadingTranscription,
    currentChat,
    maiaAgent,
    sideContent,
    externalMessage,
    externalAudioBlob,
    ratedMessages: storeRatedMessages,
  } = useSelector(state => state.merlin);
  const { id, firstname, email } = useSelector(state => state.summary);
  const { jwt } = useSelector(state => state.auth);

  const inhouseFeatures = useFeatureValue(ATIVACAO_N8N_INHOUSE_INITIAL_SCREEN);
  const wssToggleRoutes = useFeatureValue(ATIVACAO_N8N_INHOUSE_URLS);
  const initScreen = inhouseFeatures && inhouseFeatures.withHome ? SCREENS.INITIAL : SCREENS.CHAT_HISTORY;
  const disableIfOptions = useFeatureIsOn(ATIVACAO_DISABLE_TEXTEAREA_IF_OPTIONS);
  const canShowAudioButton = useFeatureIsOn(ATIVACAO_AICHAT_AUDIO_RECORDING);
  const ttrWs = useFeatureValue(ATIVACAO_N8N_TIME_TO_RECONNECT);
  const withSurvey = useFeatureIsOn(ATIVACAO_AICHAT_RATING_SURVEY);
  const canOpenChatOnLogin = useFeatureIsOn(ATIVACAO_OPEN_CHAT_ON_LOGIN);

  const [currentScreen, setCurrentScreen] = useState(history.length ? SCREENS.CHAT_HISTORY : initScreen);
  const [merlinInput, setMerlinInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [disabledForReconnect, setDisabledForReconnect] = useState(false);
  const [threadid, setThreadid] = useState((currentChat.threadid) || null);
  const [loadingMessage, setLoadingMessage] = useState();
  const [withWebSocket, setWithWebsocket] = useState(true);
  const [env, setEnv] = useState(null);
  const [route, setRoute] = useState(currentChat.route || (wssToggleRoutes && wssToggleRoutes.general) || WSS_ROUTES.GENERAL);
  const [wsAttempts, setWithWebsocketAttempts] = useState(0);
  const [recording, setRecording] = useState(false);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const [ratedMessages, setRatedMessages] = useState(storeRatedMessages);
  const [showSurvey, setShowSurvey] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  const { aichat: generalChatLocale, marketing: marketingLocale, routes: routesLocale } = useLocale();
  const [aichatLocale, setAiChatLocale] = useState(generalChatLocale);

  const redirectHistory = useHistory();

  const windowRect = useResize();
  const { width } = windowRect;
  const isMobile = width < 600;

  const localesMap = useMemo(() => ({
    general: generalChatLocale,
    marketing: { ...generalChatLocale, ...marketingLocale.maiaLocales },
  }), [generalChatLocale, marketingLocale]);

  const handleOpenHotjarSurvey = useCallback(() => {
    setShowSurvey(true);
    collect({
      event: 'aiChatHotJar',
      gaEventCategory: 'Como você avalia a ajuda da Maia?',
      gaEventAction: 'Como você avalia a ajuda da Maia?',
      gaEventLabel: 'Como você avalia a ajuda da Maia?',
    });
  }, []);

  const updateCurrentChatStore = useCallback(() => {
    dispatch(merlinActions.inhouse.setCurrentChatState({
      route,
      fullScreen: currentChat.fullScreen,
      threadid,
    }));
  }, [
    dispatch,
    route,
    threadid,
    (currentChat.fullScreen),
  ]);

  useEffect(() => {
    updateCurrentChatStore();
  }, [updateCurrentChatStore]);

  const handleEnv = useCallback(() => {
    if (ENVIRONMENT === 'development') {
      if (WSS_AI_ENV) {
        return setEnv(WSS_AI_ENV);
      }
      return setEnv('homolog');
    }

    if (ENVIRONMENT === 'production') {
      return setEnv('production');
    }
  }, []);

  useEffect(() => {
    handleEnv();
  }, [handleEnv]);

  const updateHistory = useCallback(({ message, src }) => {
    const BotMessage = {
      ...message,
      src,
    };
    dispatch(merlinActions.inhouse.setMessage(BotMessage));
  }, [
    dispatch,
  ]);

  const handleCloseClick = useCallback(({ withTimeout = true }) => {
    handleToggleMinimize(false);
    setVisibleAiChat(false);

    if (withTimeout) {
      setTimeout(() => {
        handleClose && handleClose();
      }, 350);
    }

    if (!withTimeout) {
      handleClose && handleClose();
    }
  }, [
    handleClose,
    handleToggleMinimize,
    setVisibleAiChat,
  ]);

  const handleOpenSupport = useCallback(({ params }) => {
    dispatch(preChatActions.n8n.forceOpenSupport({ bool: true, n8nParams: params }));
  }, [
    dispatch,
  ]);

  const handleRedirectToMigration = useCallback((params) => {
    handleToggleMinimize(false);
    setVisibleAiChat(false);

    if (params) {
      dispatch(merlinActions.setMigrationData({
        hostingId: params.hosting_id,
        domain: params.domain,
        type: params.type,
      }));

      dispatch(onboardingActions.wizard.register.request({
        domain: params.domain,
        origin: 'ai_agents',
      }));

      redirectHistory.push(`${routesLocale.sitesPage}${routesLocale.sitesCreate}`);
      handleClose && handleClose();
    }
  }, [dispatch, redirectHistory, routesLocale.sitesCreate, routesLocale.sitesPage, handleToggleMinimize, setVisibleAiChat, handleClose]);

  const handleRedirect = useCallback(({ params }) => {
    if (params.url) {
      window.open(params.url, '_blank');
    }

    if (params.internal) {
      if (params.internal === INTERNAL_REDIRECT_OPTIONS.BILLING) {
        history.push(`${routesLocale.invoices}${routesLocale.unpaid}`);
      }
    }
  }, [
    history,
    routesLocale.invoices,
    routesLocale.unpaid,
  ]);

  const executeAction = useCallback(({ action, params }) => {
    const actions = {
      closeChat: () => handleCloseClick({ withTimout: false }),
      openSupport: () => handleOpenSupport({ params }),
      migration: () => handleRedirectToMigration(params),
      redirect: () => handleRedirect({ params }),
    };

    return actions[action]();
  }, [
    handleCloseClick,
    handleOpenSupport,
    handleRedirectToMigration,
    handleRedirect,
  ]);

  const handleWsMessage = useCallback((message) => {
    const isArray = typeof message === 'object' && Array.isArray(message);

    const handleType = (msgData) => {
      const rules = {
        loading: () => { setLoading(true); setLoadingMessage(aichatLocale.loadingMessages[msgData.text] || aichatLocale.loadingMessages.default); },
        status: () => { setLoading(msgData.status === 'processing'); },
        text: () => { updateHistory({ message: msgData, src: 'bot' }); },
        options: () => { updateHistory({ message: msgData, src: 'bot' }); },
        action: () => { executeAction({ action: msgData.action, params: msgData.params }); },
        steps: () => { updateHistory({ message: msgData, src: 'bot' }); },
        image: () => updateHistory({ message: msgData, src: 'bot' }),
        file: () => updateHistory({ message: msgData, src: 'bot' }),
      };

      if (rules[msgData.type]) {
        rules[msgData.type]();
      }
    };

    if (isArray) {
      message.forEach((msg) => {
        handleType(msg);
      });
    }

    if (!isArray) {
      handleType(message);
    }
  }, [
    updateHistory,
    setLoading,
    setLoadingMessage,
    executeAction,
    aichatLocale,
  ]);

  const openSideContent = useCallback(({ url }) => {
    dispatch(merlinActions.inhouse.setSideContent({ isOpen: true, url }));
    !isMobile && dispatch(merlinActions.inhouse.setCurrentChatState({ fullScreen: true }));
  }, [dispatch, isMobile]);

  const handleCloseSideContent = useCallback(() => {
    dispatch(merlinActions.inhouse.setSideContent({ isOpen: false }));
  }, [dispatch]);

  const connectToWs = useCallback(({ duringSubmit, sendToWs, pullingFlow }) => {
    const hasConnection = socket && socket.current && socket.current.readyState === WebSocket.OPEN;

    const closeSocket = () => new Promise((resolve) => {
      if (hasConnection) {
        setDisabledForReconnect(true);
        socket.current.onclose = () => {
          resolve();
        };
        socket.current.close();
      } else {
        resolve();
      }
    });

    const handleConnection = () => {
      const wssUrl = `${N8N_WSS}?token=${jwt}&clientId=${id}&brand=${COUNTRY.toLowerCase()}`;

      socket.current = new WebSocket(wssUrl);

      socket.current.onopen = () => {
        setDisabledForReconnect(false);
        !IS_PROD && console.info('Websocket - conectado - onopen');
        if (duringSubmit) {
          sendToWs && sendToWs();
        }
      };

      socket.current.onmessage = (event) => {
        !IS_PROD && console.info('WebSocket - conexão onmessage - onmessage');
        const jsonObject = JSON.parse(event.data);
        handleWsMessage(jsonObject);
      };

      socket.current.onclose = () => {
        !IS_PROD && console.info('WebSocket - conexão fechada - onclose');
        socket && socket.current && socket.current.close();
        setWithWebsocketAttempts(prevAttempts => prevAttempts + 1);
        if (history && !!history.length && history[history.length - 1].type === 'loading') {
          connectToWs && connectToWs({ duringSubmit, sendToWs, pullingFlow });
        }
      };

      socket.current.onerror = (error) => {
        !IS_PROD && console.info('WebSocket - conexão encerrada - oncerror');
        logger.error('websocket error', error);
        setWithWebsocket(false);
        if (duringSubmit) {
          pullingFlow && pullingFlow();
        }
        socket && socket.current && socket.current.close();
        dispatch(authActions.tokens.jwt.update());
      };
    };

    if (hasConnection) {
      setDisabledForReconnect(true);
      !IS_PROD && console.info('Websocket - forçando reconexão');
      closeSocket().then(() => {
        handleConnection();
      });
    }

    if (!hasConnection) {
      handleConnection();
    }

    return () => {
      !IS_PROD && console.info('WebSocket - unmount');
      socket && socket.current && socket.current.close();
    };
  }, [
    handleWsMessage,
    setWithWebsocket,
    history,
    jwt,
    dispatch,
    id,
  ]);

  const clearWsInterval = useCallback(() => {
    clearTimeout(reconnectTimeoutRef.current);
    clearInterval(restartIntervalRef.current);
    socket && socket.current && socket.current && socket.current.close();
    setWithWebsocket(false);
  }, [
    socket,
  ]);

  useEffect(() => {
    setAiChatLocale(localesMap[maiaAgent] || localesMap.general);
  }, [maiaAgent, generalChatLocale, marketingLocale, localesMap]);

  useEffect(() => {
    if (wsAttempts >= 3) {
      clearWsInterval();
    }
  }, [wsAttempts, clearWsInterval]);

  useEffect(() => {
    if (restartIntervalRef.current) {
      clearWsInterval();
    }

    connectToWs({});

    const ttrInMinutes = ttrWs * 60 * 1000;
    restartIntervalRef.current = setInterval(() => {
      !IS_PROD && console.log('WebSocket - restarting connection');
      connectToWs({});
    }, ttrInMinutes);

    return () => {
      clearWsInterval();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jwt]);

  useEffect(() => {
    if (currentChat && !currentChat.threadid) {
      const generatedThreadId = threadid || `thread_${Date.now().toString()}`;
      setThreadid(generatedThreadId);
    }
  }, [threadid, currentChat]);

  const entranceAnimation = () => {
    setTimeout(() => {
      setVisibleAiChat(true);
    }, 250);
  };

  useEffect(() => {
    entranceAnimation();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    dispatch(merlinActions.requestMerlinToken());
  }, [dispatch]);

  const handleSubmitMerlin = useCallback((event, blockHistoryUpdate = false) => {
    const eventIsString = event && typeof event === 'string';

    if (!eventIsString) {
      event && event.preventDefault();
    }

    const hasConnection = withWebSocket && socket.current && socket.current.readyState === WebSocket.OPEN;

    const messageContent = eventIsString ? event : merlinInput;

    const handleHistory = () => {
      if (!blockHistoryUpdate) {
        const clientMessage = {
          type: 'text',
          text: messageContent,
          src: 'client',
        };
        dispatch(merlinActions.inhouse.setMessage(clientMessage));
      }
      setMerlinInput('');
      setCurrentScreen(SCREENS.CHAT_HISTORY);
    };

    const pullingFlow = () => {
      dispatch(merlinActions.inhouse.sendMessage({
        chatInput: messageContent,
        token,
        accountId: id,
        threadId: threadid,
        env,
        brand: COUNTRY.toLowerCase(),
        url: ENVIRONMENT === 'development' ? DEVELOPMENT_ENV.URL : undefined,
        name: firstname,
        email,
        setLoading,
        route,
      }));

      handleHistory();
    };

    const sendToWs = () => {
      const currentSocket = socket && socket.current;
      if (currentSocket) {
        socket.current.send(
          JSON.stringify({
            action: 'sendmessage',
            message: messageContent,
            token,
            accountId: `${id}`,
            threadId: threadid,
            name: firstname,
            email,
            env,
            route,
            apiUrl: DEVELOPMENT_ENV.WSS_URL,
            brand: COUNTRY.toLowerCase(),
          }),
        );

        handleHistory();
      }

      if (!currentSocket) {
        setTimeout(() => {
          sendToWs();
        }, 1000);
      }
    };

    if (token && (merlinInput || eventIsString) && threadid) {
      if (withWebSocket) {
        if (!hasConnection) {
          connectToWs({ duringSubmit: true, sendToWs, pullingFlow });
        }

        if (hasConnection) {
          sendToWs();
        }
      }

      if (!withWebSocket) {
        pullingFlow();
      }
    }
  }, [
    token,
    merlinInput,
    threadid,
    id,
    firstname,
    email,
    dispatch,
    withWebSocket,
    route,
    env,
    connectToWs,
    socket,
  ]);

  useEffect(() => {
    const onExternalMessageReceived = () => {
      if (externalMessage && token && route && env && threadid) {
        handleSubmitMerlin(externalMessage, true);
        dispatch(merlinActions.inhouse.setExternalMessage(''));
      }
    };

    onExternalMessageReceived();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalMessage, token, route, env, threadid]);

  const handleEnter = (event) => {
    if (event.key === 'Enter' && merlinInput) {
      handleSubmitMerlin(event);
    }
  };

  const toggleMinimize = () => {
    handleToggleMinimize(true);
    setVisibleAiChat(!visibleAiChat);
  };

  const handleRateMessage = ({ messageId, rating }) => {
    const ratings = [...ratedMessages];
    const existingRating = ratings.findIndex(item => item.messageId === messageId);

    if (existingRating !== -1) {
      ratings[existingRating] = { messageId, rating };
    } else {
      ratings.push({ messageId, rating });
    }

    setRatedMessages(ratings);

    dispatch(merlinActions.inhouse.rateMessage({
      url: ENVIRONMENT === 'development' ? DEVELOPMENT_ENV.URL : undefined,
      env,
      threadId: threadid,
      accountId: id,
      token: jwt,
      messageId,
      rating,
      apiAuthKey: token,
      brandName: COUNTRY.toLowerCase(),
      ratedMessages: ratings,
    }));
  };

  const toggleFullScreen = () => {
    dispatch(merlinActions.inhouse.toggleFullScreen());
  };

  const isOptionsOrSteps = history
    && !!history.length
    && (history[history.length - 1].type === 'options' || history[history.length - 1].type === 'steps')
    && (disableIfOptions || history[history.length - 1].type === 'steps');

  const resetRecording = (recorder) => {
    if (!recorder || !recorder.stream) return;
    recorder.stream.getTracks().forEach(track => track.stop());
    recorderRef.current = null;
    chunksRef.current = [];
  };

  const sendAudio = useCallback((blob) => {
    const file = new File([blob], 'audio.wav');

    dispatch(merlinActions.inhouse.sendAudioMessage({
      file,
      token,
      accountId: id,
      threadId: threadid,
      env,
      brand: COUNTRY.toLowerCase(),
      url: ENVIRONMENT === 'development' ? DEVELOPMENT_ENV.URL : undefined,
    }));
  }, [dispatch, token, id, threadid, env]);

  useEffect(() => {
    if (externalAudioBlob && token && route && env && threadid) {
      sendAudio(externalAudioBlob);
      dispatch(merlinActions.inhouse.setExternalAudioBlob(null));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalAudioBlob, token, route, env, threadid]);

  const handleStartRecording = useCallback(() => {
    chunksRef.current = [];
    setRecording(true);

    navigator.mediaDevices.getUserMedia({ audio: true })
      .then((stream) => {
        const recorder = new MediaRecorder(stream);

        recorder.ondataavailable = (e) => {
          if (e.data.size > 0) {
            chunksRef.current.push(e.data);
          }
        };

        recorderRef.current = recorder;
        recorder.start();
      });
  }, []);

  const handleStopRecording = useCallback(({ cancelRecording }) => {
    const recorder = recorderRef.current;
    if (!recorder) return;

    setRecording(false);

    recorder.onstop = () => {
      if (!cancelRecording && chunksRef.current.length > 0) {
        const audioBlob = new Blob(chunksRef.current, { type: 'audio/wav' });
        sendAudio(audioBlob);
      }
      resetRecording(recorder);
    };

    recorder.stop();
  }, [sendAudio]);

  useEffect(() => {
    if (audioTranscription) {
      setMerlinInput(audioTranscription);
      dispatch(merlinActions.inhouse.clearAudioTranscription());
    }
  }, [audioTranscription, dispatch]);

  const shouldShowAudioRecord = recording || loadingTranscription;
  const isInputDisabled = loading || !token || isOptionsOrSteps || disabledForReconnect || shouldShowAudioRecord;

  const mountForm = () => (
    <Styles.FormWrapper>
      <Styles.Form
        onSubmit={handleSubmitMerlin}
        $disabled={loading || !token || isOptionsOrSteps || disabledForReconnect}
        $isFullScreen={currentChat.fullScreen}
      >
        {loadingTranscription && (
          <TranscriptionSkeleton />
        )}
        <Styles.TextArea
          lines="3"
          value={merlinInput}
          onChange={e => setMerlinInput(e.target.value)}
          placeholder={aichatLocale.askMaiaPlaceholder}
          onKeyDown={handleEnter}
          disabled={isInputDisabled}
          autoFocus
          data-testid="textarea"
        />
        {!shouldShowAudioRecord && (
          <Styles.ActionsWrapper>
            {canShowAudioButton && (
            <IconButton
              icon={<IconAudioRecord size="md" color={isInputDisabled ? 'highDark' : 'lowDark'} />}
              onClick={handleStartRecording}
              testId="start-audio-recording"
              isDisabled={isInputDisabled}
            />
            )}
            <Styles.SubmitWrapper>
              <Button
                type="submit"
                variant="primary"
                width="fitContent"
                label={<IconArrow direction="top" />}
                disabled={!merlinInput || isOptionsOrSteps || disabledForReconnect}
                testId="submit"
              />
            </Styles.SubmitWrapper>
          </Styles.ActionsWrapper>
        )}

        {shouldShowAudioRecord && (
        <AudioRecording
          onCancel={() => handleStopRecording({ cancelRecording: true })}
          onSend={() => handleStopRecording({ cancelRecording: false })}
          isRecording={recording}
          transcribing={loadingTranscription}
          locales={aichatLocale.audioRecording}
        />
        )}
      </Styles.Form>
    </Styles.FormWrapper>
  );

  const getAiInitScreenProps = () => ({
    name: firstname,
    aichatLocale,
    inhouseFeatures,
    setCurrentScreen,
    wssToggleRoutes,
    setRoute,
    handleSubmitMerlin,
    fullScreen: currentChat.fullScreen,
    mountForm,
  });

  const getChatHistoryProps = () => ({
    history,
    locales: aichatLocale,
    loading,
    loadingMessage: loadingMessage || aichatLocale.loadingMessages.default,
    handleCloseClick,
    handleOpenSupport,
    handleRedirectToMigration,
    handleRedirect,
    renderInHouseChat,
    ratedMessages,
    setRatedMessages,
    handleRateMessage,
    fullScreen: currentChat.fullScreen,
    handleSubmitMerlin,
    openDocument: ({ url }) => openSideContent({ url }),
    viewFullImage: aichatLocale.viewFullImage,
    name: aichatLocale.name,
  });

  const screens = {
    initScreen: <AiInitScreen {...getAiInitScreenProps()} />,
    chatHistory: <ChatHistory {...getChatHistoryProps()} />,
  };

  if (!renderInHouseChat) {
    return;
  }

  const defaultContent = () => (
    <>
      <Styles.Header $isFullScreen={currentChat.fullScreen}>
        {aichatLocale.name}
        <Tag label={aichatLocale.tag} variant="info" />

        <Styles.ButtonsWrapper>
          <Tooltip
            content={currentChat.fullScreen
              ? aichatLocale.tooltips.shrink
              : aichatLocale.tooltips.fullScreen
            }
            preferredPosition="bottomLeft"
          >
            <Styles.RoundedButtonForcedCenter
              onClick={toggleFullScreen}
              $hideInMobile
              $canShowButton={!sideContent.isOpen}
              $shouldForceCenter={!currentChat.fullScreen}
            >
              { currentChat.fullScreen
                ? <IconMiniminize size="sm" color="lowDark" />
                : <IconExpand size="sm" color="lowDark" />
              }
            </Styles.RoundedButtonForcedCenter>
          </Tooltip>

          {currentChat && !currentChat.fullScreen && !sideContent.isOpen && (
            <Tooltip content={aichatLocale.tooltips.minify} preferredPosition="bottomLeft">
              <Styles.RoundedButton onClick={toggleMinimize} $canShowButton={!sideContent.isOpen}>
                <IconMinus size="sm" color="lowDark" />
              </Styles.RoundedButton>
            </Tooltip>
          )}

          {currentChat && canOpenChatOnLogin && (
            <Tooltip
              content={aichatLocale.tooltips.settings}
              preferredPosition="bottomLeft"
            >
              <Styles.RoundedButton onClick={() => setShowSettings(prevState => !prevState)} $canShowButton={!sideContent.isOpen}>
                <IconEllipsis color="lowDark" />
              </Styles.RoundedButton>
            </Tooltip>
          )}

          {currentChat && (
            <Tooltip content={aichatLocale.tooltips.close} preferredPosition="bottomLeft">
              <Styles.RoundedButton onClick={() => handleCloseClick({ withTimeout: true })} $canShowButton={!sideContent.isOpen}>
                <IconClose color="lowDark" />
              </Styles.RoundedButton>
            </Tooltip>
          )}

        </Styles.ButtonsWrapper>
      </Styles.Header>
      {showSettings && (
        <SettingsPopover
          label={aichatLocale.showMaiaOnInit}
          onClose={() => setShowSettings(false)}
          isFullScreen={currentChat.fullScreen}
          privacyPolicy={aichatLocale.privacyPolicy}
          termsOfUse={aichatLocale.termsOfUse}
        />
      )}

      {screens[currentScreen]}

      {((currentChat && !currentChat.fullScreen) || currentScreen === SCREENS.CHAT_HISTORY) && mountForm()}

      {(withSurvey && currentScreen !== SCREENS.INITIAL) && (
      <Styles.RatingSurveyWrapper id="aiChatSurvey">
        {!showSurvey && (
          <Styles.SuveryRow $fullScreen={currentChat.fullScreen}>
            <Styles.SurveyLabel role="button">
              {aichatLocale.ratingSurveyLabel}
            </Styles.SurveyLabel>
            <Styles.SurveyCta onClick={handleOpenHotjarSurvey} role="button">
              {aichatLocale.ratingSurveyCta}
            </Styles.SurveyCta>
          </Styles.SuveryRow>
        )}
      </Styles.RatingSurveyWrapper>
      )}
    </>
  );

  if (currentChat.fullScreen) {
    return (
      <Styles.FullScreenWrapper>
        <Styles.FullScreenChatContainer>
          {defaultContent()}
        </Styles.FullScreenChatContainer>
        <SideContent
          isOpen={sideContent.isOpen}
          url={sideContent.url}
          closeSideContent={handleCloseSideContent}
          toggleFullScreen={toggleFullScreen}
          closeChat={handleCloseClick}
          toggleSettings={() => setShowSettings(prevState => !prevState)}
          locales={aichatLocale}
        />
      </Styles.FullScreenWrapper>
    );
  }

  return (
    <Styles.Wrapper $visible={visibleAiChat} $isFullScreen={currentChat.fullScreen}>
      <Styles.SlideInButton
        $visible={(!visibleAiChat && minimizedChat)}
        onClick={() => { !visibleAiChat && toggleMinimize(); }}
      >
        <Styles.MaiaIcon src={MaiaIconImage || IconAnimation} $marginTop />
        {aichatLocale.name}
      </Styles.SlideInButton>
      {defaultContent()}
      <SideContent
        isOpen={sideContent.isOpen}
        url={sideContent.url}
        closeSideContent={handleCloseSideContent}
        toggleFullScreen={toggleFullScreen}
        closeChat={handleCloseClick}
        toggleSettings={() => setShowSettings(prevState => !prevState)}
        locales={aichatLocale}
      />
    </Styles.Wrapper>
  );
};

export default AiChat;
